import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { getLeaderboard, getPlayerStats, getPlayerRank, ACHIEVEMENTS } from '../utils/leaderboardManager';

const { FiTrophy, FiStar, FiTrendingUp, FiUsers, FiHome, FiFilter, FiAward, FiClock, FiTarget } = FiIcons;

const Leaderboard = () => {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('rating');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [currentUser] = useState('Player'); // In a real app, this would come from auth

  useEffect(() => {
    setLeaderboardData(getLeaderboard(sortBy, 20));
  }, [sortBy]);

  const sortOptions = [
    { value: 'rating', label: 'Rating', icon: FiTrophy },
    { value: 'wins', label: 'Total Wins', icon: FiStar },
    { value: 'winRate', label: 'Win Rate', icon: FiTrendingUp },
    { value: 'gamesPlayed', label: 'Games Played', icon: FiUsers },
    { value: 'winStreak', label: 'Win Streak', icon: FiTarget }
  ];

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${rank}`;
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 2000) return 'text-purple-400';
    if (rating >= 1600) return 'text-yellow-400';
    if (rating >= 1400) return 'text-blue-400';
    if (rating >= 1200) return 'text-green-400';
    return 'text-gray-400';
  };

  const formatTime = (milliseconds) => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatPlayTime = (milliseconds) => {
    const hours = Math.floor(milliseconds / 3600000);
    const minutes = Math.floor((milliseconds % 3600000) / 60000);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-5xl font-bold text-white mb-4 flex items-center justify-center gap-3">
            <SafeIcon icon={FiTrophy} className="text-yellow-400" />
            Leaderboard
          </h1>
          <p className="text-gray-300 text-lg">
            Compete with players and climb the rankings
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Leaderboard */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20"
            >
              {/* Sort Controls */}
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <SafeIcon icon={FiFilter} className="text-white" />
                  <h2 className="text-xl font-bold text-white">Sort by:</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  {sortOptions.map((option) => (
                    <motion.button
                      key={option.value}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setSortBy(option.value)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors ${
                        sortBy === option.value
                          ? 'bg-blue-600 text-white'
                          : 'bg-white/10 text-gray-300 hover:bg-white/20'
                      }`}
                    >
                      <SafeIcon icon={option.icon} />
                      {option.label}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Leaderboard List */}
              <div className="p-6">
                <div className="space-y-3">
                  {leaderboardData.map((player, index) => (
                    <motion.div
                      key={player.name}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ scale: 1.02 }}
                      onClick={() => setSelectedPlayer(player)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        player.name === currentUser
                          ? 'bg-blue-600/20 border-blue-400/50'
                          : 'bg-white/5 border-white/10 hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="text-2xl font-bold w-12 text-center">
                            {getRankIcon(index + 1)}
                          </div>
                          <div>
                            <h3 className="text-white font-bold text-lg">
                              {player.name}
                              {player.name === currentUser && (
                                <span className="text-blue-400 text-sm ml-2">(You)</span>
                              )}
                            </h3>
                            <div className="flex items-center gap-4 text-sm text-gray-300">
                              <span className={`font-semibold ${getRatingColor(player.rating)}`}>
                                {player.rating} Rating
                              </span>
                              <span>{player.wins}W-{player.losses}L-{player.draws}D</span>
                              <span>{player.winRate.toFixed(1)}% WR</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-white">
                            {sortBy === 'rating' && player.rating}
                            {sortBy === 'wins' && player.wins}
                            {sortBy === 'winRate' && `${player.winRate.toFixed(1)}%`}
                            {sortBy === 'gamesPlayed' && player.gamesPlayed}
                            {sortBy === 'winStreak' && player.longestWinStreak}
                          </div>
                          <div className="text-sm text-gray-400">
                            {player.achievements.length} achievements
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Player Stats Sidebar */}
          <div className="space-y-6">
            {/* Current User Quick Stats */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
            >
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <SafeIcon icon={FiStar} className="text-yellow-400" />
                Your Stats
              </h3>
              {(() => {
                const userStats = getPlayerStats(currentUser);
                const userRank = getPlayerRank(currentUser, sortBy);
                return (
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Rank:</span>
                      <span className="text-white font-semibold">
                        {userRank ? `#${userRank}` : 'Unranked'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Rating:</span>
                      <span className={`font-semibold ${getRatingColor(userStats.rating)}`}>
                        {userStats.rating}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Games:</span>
                      <span className="text-white">{userStats.gamesPlayed}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Win Rate:</span>
                      <span className="text-green-400">{userStats.winRate.toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Win Streak:</span>
                      <span className="text-orange-400">{userStats.currentWinStreak}</span>
                    </div>
                  </div>
                );
              })()}
            </motion.div>

            {/* Recent Achievements */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
            >
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <SafeIcon icon={FiAward} className="text-purple-400" />
                Achievements
              </h3>
              {(() => {
                const userStats = getPlayerStats(currentUser);
                const userAchievements = userStats.achievements.map(id => 
                  Object.values(ACHIEVEMENTS).find(achievement => achievement.id === id)
                ).filter(Boolean);

                return userAchievements.length > 0 ? (
                  <div className="space-y-2">
                    {userAchievements.slice(0, 5).map((achievement) => (
                      <div
                        key={achievement.id}
                        className="flex items-center gap-3 p-3 bg-white/5 rounded-lg"
                      >
                        <span className="text-2xl">{achievement.icon}</span>
                        <div>
                          <div className="text-white font-semibold text-sm">
                            {achievement.name}
                          </div>
                          <div className="text-gray-400 text-xs">
                            {achievement.description}
                          </div>
                        </div>
                      </div>
                    ))}
                    {userAchievements.length > 5 && (
                      <div className="text-center text-gray-400 text-sm">
                        +{userAchievements.length - 5} more achievements
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">
                    No achievements yet. Start playing to earn them!
                  </p>
                );
              })()}
            </motion.div>

            {/* Navigation */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-3"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/')}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
              >
                <SafeIcon icon={FiHome} />
                Back to Menu
              </motion.button>
            </motion.div>
          </div>
        </div>

        {/* Player Detail Modal */}
        <AnimatePresence>
          {selectedPlayer && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
              onClick={() => setSelectedPlayer(null)}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 rounded-2xl shadow-2xl border border-white/20 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              >
                <div className="text-center mb-6">
                  <h2 className="text-3xl font-bold text-white mb-2">
                    {selectedPlayer.name}
                  </h2>
                  <div className={`text-2xl font-bold ${getRatingColor(selectedPlayer.rating)}`}>
                    {selectedPlayer.rating} Rating
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div className="space-y-3">
                    <h3 className="text-lg font-bold text-white border-b border-white/20 pb-2">
                      Game Stats
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Games Played:</span>
                        <span className="text-white">{selectedPlayer.gamesPlayed}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Wins:</span>
                        <span className="text-green-400">{selectedPlayer.wins}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Losses:</span>
                        <span className="text-red-400">{selectedPlayer.losses}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Draws:</span>
                        <span className="text-yellow-400">{selectedPlayer.draws}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Win Rate:</span>
                        <span className="text-white">{selectedPlayer.winRate.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-lg font-bold text-white border-b border-white/20 pb-2">
                      Performance
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Current Streak:</span>
                        <span className="text-orange-400">{selectedPlayer.currentWinStreak}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Best Streak:</span>
                        <span className="text-orange-400">{selectedPlayer.longestWinStreak}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Avg Game Time:</span>
                        <span className="text-white">
                          {formatTime(selectedPlayer.avgGameTime)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Total Play Time:</span>
                        <span className="text-white">
                          {formatPlayTime(selectedPlayer.totalPlayTime)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Fastest Win:</span>
                        <span className="text-green-400">
                          {selectedPlayer.fastestWin ? formatTime(selectedPlayer.fastestWin) : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Player Achievements */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-white border-b border-white/20 pb-2 mb-4">
                    Achievements ({selectedPlayer.achievements.length})
                  </h3>
                  {selectedPlayer.achievements.length > 0 ? (
                    <div className="grid grid-cols-2 gap-3">
                      {selectedPlayer.achievements.map(achievementId => {
                        const achievement = Object.values(ACHIEVEMENTS).find(a => a.id === achievementId);
                        return achievement ? (
                          <div
                            key={achievement.id}
                            className="flex items-center gap-3 p-3 bg-white/5 rounded-lg"
                          >
                            <span className="text-xl">{achievement.icon}</span>
                            <div>
                              <div className="text-white font-semibold text-sm">
                                {achievement.name}
                              </div>
                              <div className="text-gray-400 text-xs">
                                {achievement.description}
                              </div>
                            </div>
                          </div>
                        ) : null;
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">No achievements earned yet.</p>
                  )}
                </div>

                <div className="text-center">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedPlayer(null)}
                    className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    Close
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Leaderboard;